// Layout of a project's Yjs document, and the schemas used to read it safely.
// Spec §5.1. Everything collaborative lives here; SQLite holds accounts, files and history.
import * as Y from 'yjs';
import { z } from 'zod';
import { MAP_LIMITS } from './geo.js';

export const ROOT = { cards: 'cards', boards: 'boards', spark: 'spark', routes: 'routes', meta: 'meta' } as const;
export const SHARED_BOARDS = { research: 'bd_research', moodboard: 'bd_moodboard' } as const;
export const BOARD_KINDS = ['research', 'moodboard', 'angles'] as const;
export type BoardKind = (typeof BOARD_KINDS)[number];

// M1 card kinds plus M2's 'finding' and M3's 'place'. M4 adds 'angle'.
export const CARD_KINDS = ['note', 'image', 'link', 'palette', 'todo', 'finding', 'place'] as const;
export type CardKind = (typeof CARD_KINDS)[number];

export const LIMITS = { text: 20_000, title: 200, tags: 20, comment: 4000, colours: 8, todoItems: 100, claim: 2000, sources: 20, findingImages: 4, address: 500, placeNotes: 2000 } as const;

/** Normalises a tag list the same way everywhere it's set: trimmed, deduplicated, each ≤40 characters, at most LIMITS.tags. */
export const cleanTags = (tags: string[]): string[] => [...new Set(tags.map((t) => t.trim().slice(0, 40)).filter(Boolean))].slice(0, LIMITS.tags);

/** Fixed-ID columns, one per board at most: where accepted findings and agent cards go unless placed. */
export const FIXED_COLUMNS = { inbox: 'cl_inbox', agent: 'cl_agent' } as const;

export const actorSchema = z.object({
  kind: z.enum(['human', 'agent']),
  userId: z.string(),
  name: z.string(),
  tokenId: z.string().optional(),
});
export type Actor = z.infer<typeof actorSchema>;

export const httpUrl = z.string().max(2048).refine((u) => /^https?:\/\//i.test(u), 'http(s) only');
const iso = z.string();

const ACCESSED_AT_RE = /^(\d{4})-(\d{2})-(\d{2})(T[0-9:.]+(Z|[+-]\d{2}:\d{2})?)?$/;

/** When the source was read: a calendar date (2026-10-13) or a full ISO timestamp. */
export const accessedAtSchema = z
  .string()
  .max(40)
  .refine((s) => {
    const m = ACCESSED_AT_RE.exec(s);
    if (!m || Number.isNaN(Date.parse(s))) return false;
    const [, y, mo, d] = m;
    // Date.parse rolls an impossible calendar date (2026-02-30) forward instead of rejecting it
    // (e.g. to 2026-03-02); round-trip the date-only part through UTC getters to catch that.
    const check = new Date(`${y}-${mo}-${d}T00:00:00Z`);
    return check.getUTCFullYear() === Number(y) && check.getUTCMonth() + 1 === Number(mo) && check.getUTCDate() === Number(d);
  }, 'accessedAt must be a date such as 2026-10-13');
export const sourceSchema = z.object({ url: httpUrl, title: z.string().max(300).default(''), accessedAt: accessedAtSchema });
export type Source = z.infer<typeof sourceSchema>;
/** A web image kept as a credited link plus a small cached thumbnail; never the full-size original. */
export const webImageSchema = z.object({
  url: httpUrl,
  sourcePageUrl: httpUrl.nullable().default(null),
  credit: z.string().max(300).default(''),
  thumbAssetId: z.string().nullable().default(null),
});
export type WebImage = z.infer<typeof webImageSchema>;
export const FINDING_STATUSES = ['researched', 'confirmed', 'question'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];
export const TRAY_STATES = ['suggested', 'accepted', 'dismissed'] as const;
export type TrayState = (typeof TRAY_STATES)[number];

export const commentSchema = z.object({ id: z.string(), author: actorSchema, text: z.string().max(LIMITS.comment), at: iso });
export type Comment = z.infer<typeof commentSchema>;

const base = {
  id: z.string(),
  createdBy: actorSchema,
  createdAt: iso,
  updatedBy: actorSchema,
  updatedAt: iso,
  tags: z.array(z.string().max(40)).max(LIMITS.tags).default([]),
  placeCardId: z.string().nullable().default(null),
  comments: z.array(commentSchema).default([]),
  // Flat keys "<emoji>|<userId or an agent's tokenId>" → true (see reactorId in ops.ts). A nested
  // map per emoji would lose reactions when two people create the same emoji's map concurrently
  // (last writer wins on the parent key).
  reactions: z.record(z.string(), z.literal(true)).default({}),
};

/** A swatch in a palette card: a hex colour and an optional name. */
export const colourSchema = z.object({ hex: z.string().regex(/^#[0-9a-f]{6}$/i), name: z.string().max(40).default('') });
export type Colour = z.infer<typeof colourSchema>;

export const cardSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('note'), body: z.string() }), // Y.XmlFragment.toJSON() is its XML string
  z.object({
    ...base,
    kind: z.literal('image'),
    assetId: z.string().nullable().default(null), // uploaded image
    url: httpUrl.nullable().default(null), // web image (M2)
    sourcePageUrl: httpUrl.nullable().default(null),
    thumbAssetId: z.string().nullable().default(null),
    credit: z.string().max(300).default(''),
    caption: z.string().max(1000).default(''),
    width: z.number().int().nonnegative().default(0),
    height: z.number().int().nonnegative().default(0),
  }),
  z.object({
    ...base,
    kind: z.literal('link'),
    url: httpUrl,
    title: z.string().max(LIMITS.title).default(''),
    description: z.string().max(1000).default(''),
    siteName: z.string().max(100).default(''),
    imageAssetId: z.string().nullable().default(null),
    previewStatus: z.enum(['pending', 'ok', 'failed']).default('pending'),
  }),
  z.object({
    ...base,
    kind: z.literal('palette'),
    colours: z.array(colourSchema).min(1).max(LIMITS.colours),
  }),
  z.object({
    ...base,
    kind: z.literal('todo'),
    title: z.string().max(LIMITS.title).default(''),
    items: z.array(z.object({ id: z.string(), text: z.string().max(500), done: z.boolean() })).max(LIMITS.todoItems).default([]),
  }),
  z.object({
    ...base,
    kind: z.literal('finding'),
    claim: z.string().min(1).max(LIMITS.claim),
    sources: z.array(sourceSchema).min(1).max(LIMITS.sources),
    status: z.enum(FINDING_STATUSES),
    trayState: z.enum(TRAY_STATES),
    images: z.array(webImageSchema).max(LIMITS.findingImages).default([]),
    // Who accepted or dismissed it, and when (null while suggested).
    trayBy: actorSchema.nullable().default(null),
    trayAt: iso.nullable().default(null),
  }),
  z.object({
    ...base,
    kind: z.literal('place'),
    // May be empty while someone is typing it; shown as "Untitled place".
    name: z.string().max(LIMITS.title).default(''),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    address: z.string().max(LIMITS.address).default(''),
    notes: z.string().max(LIMITS.placeNotes).default(''),
  }),
]);
export type Card = z.infer<typeof cardSchema>;

export function parseCard(json: unknown): Card | null {
  const r = cardSchema.safeParse(json);
  return r.success ? r.data : null;
}

/**
 * Groups flat reaction keys into emoji → reactor IDs, in first-seen order. `userIds` holds a
 * person's user ID, or (kept as `userIds` since client code already reads this shape) an agent's
 * token ID — see the `reactions` field above and `reactorId` in ops.ts.
 */
export function reactionsByEmoji(reactions: Record<string, true>): { emoji: string; userIds: string[] }[] {
  const out = new Map<string, string[]>();
  for (const key of Object.keys(reactions)) {
    const i = key.lastIndexOf('|');
    if (i <= 0) continue;
    const emoji = key.slice(0, i);
    out.set(emoji, [...(out.get(emoji) ?? []), key.slice(i + 1)]);
  }
  return [...out].map(([emoji, userIds]) => ({ emoji, userIds }));
}

export const posSchema = z.object({ x: z.number().finite(), y: z.number().finite() });
export type Pos = z.infer<typeof posSchema>;
export const sizeSchema = z.object({ w: z.number().min(120).max(1600), h: z.number().min(60).max(2000).nullable() });
export type Size = z.infer<typeof sizeSchema>;

export const placementSchema = z.object({
  pos: posSchema,
  size: sizeSchema,
  z: z.number(),
  columnId: z.string().nullable().default(null),
});
export type Placement = z.infer<typeof placementSchema>;
export function parsePlacement(json: unknown): Placement | null {
  const r = placementSchema.safeParse(json);
  return r.success ? r.data : null;
}

export const columnSchema = z.object({ title: z.string().max(LIMITS.title), pos: posSchema, w: z.number(), cardIds: z.array(z.string()) });
export type Column = z.infer<typeof columnSchema>;
export const arrowSchema = z.object({ from: z.string(), to: z.string() });
export type Arrow = z.infer<typeof arrowSchema>;

export const DEFAULT_SIZE: Record<CardKind, Size> = {
  note: { w: 260, h: null },
  image: { w: 280, h: null },
  link: { w: 280, h: null },
  palette: { w: 240, h: null },
  todo: { w: 260, h: null },
  finding: { w: 300, h: null },
  place: { w: 240, h: null },
};

export const cardsMap = (doc: Y.Doc) => doc.getMap<Y.Map<any>>(ROOT.cards);
export const boardsMap = (doc: Y.Doc) => doc.getMap<Y.Map<any>>(ROOT.boards);
export const sparkArray = (doc: Y.Doc) => doc.getArray<string>(ROOT.spark);
export const metaMap = (doc: Y.Doc) => doc.getMap<unknown>(ROOT.meta);
export const boardMap = (doc: Y.Doc, boardId: string) => boardsMap(doc).get(boardId);

// --- Routes (spec §5.1, §9, §13) -----------------------------------------------------------------

export const ROUTE_SOURCES = ['drawn', 'gpx', 'routed'] as const;
export type RouteSource = (typeof ROUTE_SOURCES)[number];

const lonLatSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

/**
 * A route in the live document. Only the simplified `displayLine` lives here (one array of [lon, lat]
 * per part, at most 2000 points in all); a GPX route's full-resolution geometry, with elevation and
 * time, is in SQLite's route_geometry, matched by `geometryHash`. Drawn routes keep their
 * `waypoints`, which are their full geometry.
 */
export const routeSchema = z.object({
  id: z.string(),
  name: z.string().max(LIMITS.title).default(''),
  source: z.enum(ROUTE_SOURCES),
  colour: z.string().regex(/^#[0-9a-f]{6}$/i),
  waypoints: z.array(lonLatSchema).max(MAP_LIMITS.waypoints).nullable().default(null),
  displayLine: z
    .array(z.array(lonLatSchema).min(2))
    .min(1)
    .refine((parts) => parts.reduce((n, p) => n + p.length, 0) <= MAP_LIMITS.displayPoints, `A route's display line holds at most ${MAP_LIMITS.displayPoints} points`),
  lengthM: z.number().nonnegative(),
  geometryHash: z.string().max(100),
  /** The original GPX file (an asset of kind "gpx"), for routes imported from one. */
  assetId: z.string().nullable().default(null),
  createdBy: actorSchema,
  createdAt: iso,
  updatedBy: actorSchema,
  updatedAt: iso,
});
export type Route = z.infer<typeof routeSchema>;

export function parseRoute(json: unknown): Route | null {
  const r = routeSchema.safeParse(json);
  return r.success ? r.data : null;
}

export const routesMap = (doc: Y.Doc) => doc.getMap<Y.Map<any>>(ROOT.routes);
